import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  InputAdornment,
  IconButton,
  Paper,
  Fade,
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  alpha,
} from '@mui/material';
import {
  Email,
  Phone,
  Security,
  Visibility,
  VisibilityOff,
  LockReset,
  CheckCircle,
  ArrowBack,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { authService } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { ResetPasswordRequest } from '../../types/index';

const OTPVerificationDialog: React.FC<{
  showOTPDialog: boolean;
  registeredEmail: string;
  otpCode: string;
  setOtpCode: (code: string) => void;
  otpError: string;
  otpLoading: boolean;
  timerDisplay: { text: string; color: 'primary' | 'error'; key: string };
  resendCooldown: number;
  verifyOtpSubmit: () => void;
  resendOtpSubmit: () => void;
  setShowOTPDialog: (open: boolean) => void;
}> = React.memo(({
  showOTPDialog,
  registeredEmail,
  otpCode,
  setOtpCode,
  otpError,
  otpLoading,
  timerDisplay,
  resendCooldown,
  verifyOtpSubmit,
  resendOtpSubmit,
  setShowOTPDialog,
}) => (
  <Dialog
    open={showOTPDialog}
    maxWidth="sm"
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: 4,
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #E3F2FD 0%, #F3E5F5 100%)',
      },
    }}
  >
    <DialogTitle
      sx={{
        textAlign: 'center',
        pb: 1,
        pt: 4,
        background: 'linear-gradient(135deg, #1976D2 0%, #7B1FA2 100%)',
        color: 'white',
      }}
    >
      <Security sx={{ fontSize: 48, mb: 2, color: 'white' }} />
      <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
        Verify Email Address
      </Typography>
      <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
        We've sent a 6-digit Security code to {registeredEmail}
      </Typography>
    </DialogTitle>

    <DialogContent sx={{ p: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography
          variant="body2"
          color={timerDisplay.color}
          sx={{
            fontWeight: 600,
            fontFamily: 'monospace',
            fontSize: '1.1rem',
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha(timerDisplay.color === 'error' ? '#FFEBEE' : '#E3F2FD', 0.5),
            display: 'inline-block',
          }}
        >
          {timerDisplay.text}
        </Typography>
      </Box>

      {otpError && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {otpError}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Enter 6 digit Security Code"
        value={otpCode}
        onChange={(e) => {
          const value = e.target.value.replace(/\D/g, '').slice(0, 6);
          setOtpCode(value);
        }}
        placeholder="123456"
        inputProps={{
          maxLength: 6,
          style: {
            textAlign: 'center',
            fontSize: '2rem',
            letterSpacing: '0.5rem',
            fontFamily: 'monospace',
          },
        }}
        sx={{
          mb: 3,
          '& .MuiOutlinedInput-root': {
            height: '64px',
            borderRadius: 3,
            fontSize: '1.5rem',
          },
        }}
      />

      <Box sx={{ textAlign: 'center' }}>
        <Button
          variant="text"
          onClick={resendOtpSubmit}
          disabled={resendCooldown > 0 && timerDisplay.key !== 'expired'}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          {resendCooldown > 0 && timerDisplay.key !== 'expired'
            ? `Resend Security Code in ${resendCooldown}s`
            : 'Resend Security Code'
          }
        </Button>
      </Box>
    </DialogContent>

    <DialogActions sx={{ p: 3, pt: 0 }}>
      <Button
        onClick={() => setShowOTPDialog(false)}
        disabled={otpLoading}
        sx={{ borderRadius: 2 }}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        onClick={verifyOtpSubmit}
        disabled={otpLoading || otpCode.length !== 6 || timerDisplay.key === 'expired'}
        sx={{
          borderRadius: 2,
          px: 4,
          background: 'linear-gradient(135deg, #1976D2 0%, #7B1FA2 100%)',
        }}
      >
        {otpLoading ? <CircularProgress size={20} /> : 'Verify & Continue'}
      </Button>
    </DialogActions>
  </Dialog>
));

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, resetPassword, requestPasswordReset } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // OTP Verification Dialog State
  const [showOTPDialog, setShowOTPDialog] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpExpiresAt, setOtpExpiresAt] = useState<Date | null>(null);
  const [otpTimeRemaining, setOtpTimeRemaining] = useState(0);

  const steps = ['Enter Email Address', 'Verify Security Code', 'Reset Password'];

  interface ForgotPasswordFormData {
    email: string;
  }
  
  interface ResetPasswordFormData {
    password: string;
    password_confirm: string;
  }
  // Form for mobile number input
  const {
    control: mobileControl,
    handleSubmit: handleMobileSubmit,
    formState: { errors: mobileErrors, isSubmitting: mobileSubmitting },
  } = useForm<ForgotPasswordFormData>({
    defaultValues: {
      email: '',
    },
  });

  // Form for password reset
  const {
    control: passwordControl,
    handleSubmit: handlePasswordSubmit,
    watch,
    formState: { errors: passwordErrors, isSubmitting: passwordSubmitting },
  } = useForm<ResetPasswordFormData>({
    defaultValues: {
      password: '',
      password_confirm: '',
    },
  });

  const watchPassword = watch('password');

  // OTP Timer Effect
  useEffect(() => {
    if (otpExpiresAt && showOTPDialog) {
      const timer = setInterval(() => {
        const now = new Date();
        const timeLeft = Math.max(0, Math.floor((otpExpiresAt.getTime() - now.getTime()) / 1000));

        if (timeLeft !== otpTimeRemaining) {
          setOtpTimeRemaining(timeLeft);
          if (timeLeft === 0 && otpTimeRemaining > 0) {
            setOtpError('Security Code has expired. You can now resend a new Security Code.');
            setResendCooldown(0);
          }
        }

        if (timeLeft === 0) {
          clearInterval(timer);
        }
      }, 1000);

      const now = new Date();
      const initialTimeLeft = Math.max(0, Math.floor((otpExpiresAt.getTime() - now.getTime()) / 1000));
      setOtpTimeRemaining(initialTimeLeft);

      return () => clearInterval(timer);
    }
  }, [otpExpiresAt, showOTPDialog, otpTimeRemaining]);

  // Memoized timer display
  const timerDisplay = useMemo(() => {
    if (otpTimeRemaining > 0) {
      const minutes = Math.floor(otpTimeRemaining / 60);
      const seconds = otpTimeRemaining % 60;
      const timeString = `${minutes}:${seconds.toString().padStart(2, '0')}`;
      return {
        text: `Expires in: ${timeString}`,
        color: (otpTimeRemaining < 60 ? 'error' : 'primary') as 'error' | 'primary',
        key: `${minutes}-${seconds}`,
      };
    } else {
      return {
        text: 'Security Code has expired',
        color: 'error',
        key: 'expired',
      };
    }
  }, [otpTimeRemaining]);

  // Step 1: Request OTP for password reset
  const requestOtpSubmit = async (data: ForgotPasswordFormData) => {
    try {
      setError('');
      setLoading(true);
      //console.log('Email value:', data.email, 'Type:', typeof data.email);
      const response = await requestPasswordReset(data.email);
      setRegisteredEmail(data.email);
      
      // Set OTP expiration (usually 5 minutes from now)
      setOtpExpiresAt(new Date(Date.now() + 5 * 60 * 1000));

      setShowOTPDialog(true);
      setActiveStep(1);
    } catch (err: any) {     
        const errorMessage = 
            err.data?.email?.[0] ||              // "User is not found with this Email Address!"
            err.data?.error ||
            err.data?.message ||
            err.message ||
            'Failed to send Security Code. Please try again.';
        setError(errorMessage);
    } finally {
        setLoading(false);
    }
  };

  // Step 2: OTP Verification for password reset
  const verifyOtpSubmit = async () => {
    if (!otpCode || otpCode.length !== 6) {
      setOtpError('Please enter a valid 6-digit Security Code');
      return;
    }

    try {
      setOtpError('');
      setOtpLoading(true);

      await authService.verifyOTP({
        email: registeredEmail,
        otp_code: otpCode,
      });

      // OTP verified successfully
      setShowOTPDialog(false);
      setActiveStep(2);
    } catch (err: any) {
      setOtpError(err.response?.data?.message || 'Invalid Security code. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // Resend OTP
  const resendOtpSubmit = async () => {
    if (resendCooldown > 0) return;

    try {
      setOtpError('');
      setOtpCode('');

      await requestPasswordReset(registeredEmail);

      // Update OTP expiration time
      setOtpExpiresAt(new Date(Date.now() + 5 * 60 * 1000));

      // Start cooldown timer
      setResendCooldown(120);
      const timer = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      setOtpError('Failed to resend Security Code. Please try again.');
    }
  };

  // Step 3: Reset Password
  const resetPasswordSubmit = async (data: ResetPasswordFormData) => {
    try {
      setError('');
      setLoading(true);

      await resetPassword({
        email: registeredEmail,
        otp_code: otpCode,
        password: data.password,
        password_confirm: data.password_confirm
      });

      setSuccess('Password reset successfully! You can now login with your new password.');
      
      // Redirect to login after a delay
      setTimeout(() => {
        navigate('/login', {
          state: {
            message: 'Password reset successfully!',
            type: 'success',
          },
        });
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    } else {
      navigate('/login');
    }
  };

  return (
    <>
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: { xs: 2, sm: 4 },
            background: 'linear-gradient(135deg, #E3F2FD 0%, #F3E5F5 50%, #F1F8E9 100%)',
          }}
        >
          <Fade in timeout={800}>
            <Box sx={{ width: '100%' }}>
              <Paper
                elevation={12}
                sx={{
                  width: '100%',
                  borderRadius: 4,
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, #1976D2 0%, #7B1FA2 100%)',
                }}
              >
                <Box sx={{ p: { xs: 0.5, sm: 1 } }}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      boxShadow: 'none',
                    }}
                  >
                    <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
                      {/* Header */}
                      <Box textAlign="center" mb={4}>
                        <Slide direction="down" in timeout={600}>
                          <Box>
                            <LockReset
                              sx={{
                                fontSize: { xs: 40, sm: 48 },
                                color: 'primary.main',
                                mb: 2,
                                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                              }}
                            />
                            <Typography
                              variant="h4"
                              component="h1"
                              gutterBottom
                              sx={{
                                fontSize: { xs: '1.8rem', sm: '2.125rem' },
                                fontWeight: 600,
                                background: 'linear-gradient(45deg, #1976D2, #7B1FA2)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                              }}
                            >
                              Reset Password
                            </Typography>
                            <Typography
                              variant="body1"
                              color="text.secondary"
                              sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, pb:2 }}
                            >
                              Follow the steps to reset your password
                            </Typography>
                          </Box>
                        </Slide>                       
                        {/* Stepper */}
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            mb: 4,
                            borderRadius: 4,
                            background: 'rgba(221, 219, 246, 0.44)',
                            backdropFilter: 'blur(10px)',
                          }}
                        >
                          <Stepper activeStep={activeStep} alternativeLabel>
                            {steps.map((label) => (
                              <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                              </Step>
                            ))}
                          </Stepper>
                        </Paper>
                      </Box>

                      {/* Alerts */}
                      {error && (
                        <Slide direction="left" in timeout={400}>
                          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                            {error}
                          </Alert>
                        </Slide>
                      )}

                      {success && (
                        <Slide direction="left" in timeout={400}>
                          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                            {success}
                          </Alert>
                        </Slide>
                      )}

                      {/* Step 1: Email Input */}
                      {activeStep === 0 && (
                        <form onSubmit={handleMobileSubmit(requestOtpSubmit)} noValidate>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Controller
                              name="email"
                              control={mobileControl}
                              rules={{
                                required: 'Email is required',
                                pattern: {
                                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                  message: 'Please enter a valid Email (xyz@abc.com)',
                                },
                              }}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  label="Email"
                                  placeholder="xyz@abc.com"
                                  error={!!mobileErrors.email}
                                  helperText={mobileErrors.email?.message || 'We will send a Security Code to this Email Address'}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <Email color="action" />
                                      </InputAdornment>
                                    ),
                                  }}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                    },
                                  }}
                                />
                              )}
                            />

                            <Button
                              type="submit"
                              fullWidth
                              variant="contained"
                              size="large"
                              disabled={mobileSubmitting || loading}
                              sx={{
                                mt: 2,
                                py: 1.5,
                                borderRadius: 2,
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                background: 'linear-gradient(135deg, #1976D2 0%, #7B1FA2 100%)',
                                boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #1565C0 0%, #6A1B9A 100%)',
                                  boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                                  transform: 'translateY(-2px)',
                                },
                                transition: 'all 0.3s ease',
                              }}
                            >
                              {mobileSubmitting || loading ? (
                                <>
                                  <CircularProgress size={20} sx={{ mr: 1 }} />
                                  Send Security Code
                                </>
                              ) : (
                                'Send Security Code'
                              )}
                            </Button>
                          </Box>
                        </form>
                      )}

                      {/* Step 3: Password Reset */}
                      {activeStep === 2 && (
                        <form onSubmit={handlePasswordSubmit(resetPasswordSubmit)} noValidate>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Controller
                              name="password"
                              control={passwordControl}
                              rules={{
                                required: 'Password is required',
                                minLength: {
                                  value: 8,
                                  message: 'Password must be at least 8 characters',
                                },
                                pattern: {
                                  value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                                  message: 'Password must contain uppercase, lowercase, number, and special character',
                                },
                              }}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  label="New Password"
                                  type={showPassword ? 'text' : 'password'}
                                  placeholder="Min 8 chars, include A-z, 0-9, @$!%*?&"
                                  error={!!passwordErrors.password}
                                  helperText={passwordErrors.password?.message}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <LockReset color="action" />
                                      </InputAdornment>
                                    ),
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          onClick={() => setShowPassword(!showPassword)}
                                          edge="end"
                                          aria-label="toggle password visibility"
                                        >
                                          {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                    },
                                  }}
                                />
                              )}
                            />

                            <Controller
                              name="password_confirm"
                              control={passwordControl}
                              rules={{
                                required: 'Please confirm your password',
                                validate: (value) => value === watchPassword || 'Passwords do not match',
                              }}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  label="Confirm New Password"
                                  type={showConfirmPassword ? 'text' : 'password'}
                                  placeholder="Re-enter your new password"
                                  error={!!passwordErrors.password_confirm}
                                  helperText={passwordErrors.password_confirm?.message}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <LockReset color="action" />
                                      </InputAdornment>
                                    ),
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                          edge="end"
                                          aria-label="toggle confirm password visibility"
                                        >
                                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                    },
                                  }}
                                />
                              )}
                            />

                            <Button
                              type="submit"
                              fullWidth
                              variant="contained"
                              size="large"
                              disabled={passwordSubmitting || loading}
                              sx={{
                                mt: 2,
                                py: 1.5,
                                borderRadius: 2,
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                background: 'linear-gradient(135deg, #1976D2 0%, #7B1FA2 100%)',
                                boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #1565C0 0%, #6A1B9A 100%)',
                                  boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                                  transform: 'translateY(-2px)',
                                },
                                transition: 'all 0.3s ease',
                              }}
                            >
                              {passwordSubmitting || loading ? (
                                <>
                                  <CircularProgress size={20} sx={{ mr: 1 }} />
                                  Reset Password
                                </>
                              ) : (
                                'Reset Password'
                              )}
                            </Button>
                          </Box>
                        </form>
                      )}

                      {/* Navigation */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                        <Button
                          onClick={handleBack}
                          startIcon={<ArrowBack />}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': {
                              background: 'none',
                              color: 'primary.main',
                            },
                          }}
                        >
                          Back
                        </Button>

                        <Link
                          to="/login"
                          style={{
                            textDecoration: 'none',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              background: 'linear-gradient(45deg, #1976D2, #7B1FA2)',
                              backgroundClip: 'text',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            Back to Login
                          </Typography>
                        </Link>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              </Paper>
            </Box>
          </Fade>
        </Box>
      </Container>

      {/* OTP Verification Dialog */}
      <OTPVerificationDialog
        showOTPDialog={showOTPDialog}
        registeredEmail={registeredEmail}
        otpCode={otpCode}
        setOtpCode={setOtpCode}
        otpError={otpError}
        otpLoading={otpLoading}
        timerDisplay={timerDisplay as any}
        resendCooldown={resendCooldown}
        verifyOtpSubmit={verifyOtpSubmit}
        resendOtpSubmit={resendOtpSubmit}
        setShowOTPDialog={setShowOTPDialog}
      />
    </>
  );
};

export default ForgotPasswordPage;